import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  DollarSign,
  Activity,
  Calendar,
  Filter
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { analyticsService } from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const PALETTE = ['#6366f1', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444'];

const ATooltip = ({ active, payload, label, fmtVal }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 text-white rounded-xl px-3 py-2.5 shadow-xl text-xs min-w-[130px]">
      <p className="text-slate-400 mb-1.5 font-medium">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-1.5 mt-0.5">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
          <span className="text-slate-300">{p.name}:</span>
          <span style={{ color: p.color }} className="font-semibold ml-auto pl-2">
            {fmtVal ? fmtVal(p.value, p.name) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const Analytics = () => {
  const [costData, setCostData] = useState([]);
  const [performanceData, setPerformanceData] = useState(null);
  const [patternsData, setPatternsData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('365d');
  const [groupBy, setGroupBy] = useState('day');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange, groupBy, startDate, endDate]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);

      let start, end;
      if (dateRange === 'custom' && startDate && endDate) {
        start = startDate;
        end = endDate;
      } else {
        const now = new Date();
        const days = dateRange === 'all' ? 365 : (parseInt(dateRange.replace('d', '')) || 365);
        start = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
      }

      const [costs, performance, patterns, forecast] = await Promise.all([
        analyticsService.getCostAnalysis(start, end, groupBy),
        analyticsService.getPerformanceMetrics(start, end),
        analyticsService.getUsagePatterns(start, end),
        analyticsService.getForecastData(dateRange === 'all' ? 365 : (parseInt(dateRange.replace('d', '')) || 30))
      ]);

      setCostData(costs.data.data.costAnalysis);
      setPerformanceData(performance.data.data);
      setPatternsData(patterns.data.data);
      setForecastData(forecast.data.data);
    } catch (error) {
      toast.error('Failed to fetch analytics data');
      console.error('Analytics data fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  const formatCurrency = (amount) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(numAmount || 0);
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
          <p className="text-sm text-slate-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Advanced Analytics</h1>
        <p className="text-slate-500 text-sm mt-0.5">Deep insights into API usage patterns and costs</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-slate-400" />
            <span className="text-sm font-medium text-slate-600">Range:</span>
          </div>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="365d">Last 365 days</option>
            <option value="all">All time</option>
            <option value="custom">Custom range</option>
          </select>

          {dateRange === 'custom' && (
            <>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
              <span className="text-slate-400 text-sm">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </>
          )}

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-600">Group by:</span>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="model">Model</option>
              <option value="key">API Key</option>
            </select>
          </div>

          <button
            onClick={fetchAnalyticsData}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Filter size={14} />
            Apply
          </button>
        </div>
      </div>

      {/* Cost Analysis */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900 mb-4">Cost Analysis</h3>
        {costData && costData.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={costData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }} barGap={4}>
              <defs>
                <linearGradient id="aCostBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#34d399" stopOpacity={0.6} />
                </linearGradient>
                <linearGradient id="aTokenBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#818cf8" stopOpacity={0.6} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey={groupBy === 'day' ? 'date' : groupBy === 'model' ? 'model_name' : 'key_name'}
                tickFormatter={v => groupBy === 'day'
                  ? new Date(v).toLocaleDateString('en', { month: 'short', day: 'numeric' })
                  : v.slice(0, 14)}
                tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                tickFormatter={v => formatNumber(v)}
              />
              <Tooltip content={<ATooltip fmtVal={(v, n) => n === 'Tokens' ? formatNumber(v) : formatCurrency(v)} />} />
              <Bar dataKey="total_cost" fill="url(#aCostBar)" name="Cost" radius={[4, 4, 0, 0]} maxBarSize={40} />
              <Bar dataKey="total_tokens" fill="url(#aTokenBar)" name="Tokens" radius={[4, 4, 0, 0]} maxBarSize={40} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
            No cost data available
          </div>
        )}
      </div>

      {/* Performance Metrics */}
      {performanceData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h3 className="text-base font-semibold text-slate-900 mb-4">Response Time by Model</h3>
            {performanceData.responseTimeStats && performanceData.responseTimeStats.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={performanceData.responseTimeStats} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="aRespBar" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#818cf8" stopOpacity={0.6} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="model_name"
                    tickFormatter={v => v.split('.').pop().slice(0, 12)}
                    tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                  />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ATooltip fmtVal={v => `${v}ms`} />} />
                  <Bar dataKey="avg_response_time" fill="url(#aRespBar)" name="Response Time" radius={[4, 4, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
                No performance data available
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h3 className="text-base font-semibold text-slate-900 mb-4">Hourly Performance Patterns</h3>
            {performanceData.hourlyPatterns && performanceData.hourlyPatterns.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={performanceData.hourlyPatterns} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="aHourlyResp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="aHourlyCost" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.12} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ATooltip fmtVal={(v, n) => n === 'Response Time' ? `${v}ms` : formatCurrency(v)} />} />
                  <Area type="monotone" dataKey="avg_response_time" stroke="#6366f1" strokeWidth={2}
                    fill="url(#aHourlyResp)" dot={false} name="Response Time" />
                  <Area type="monotone" dataKey="total_cost" stroke="#10b981" strokeWidth={2}
                    fill="url(#aHourlyCost)" dot={false} name="Cost" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
                No hourly patterns available
              </div>
            )}
          </div>
        </div>
      )}

      {/* Usage Patterns */}
      {patternsData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h3 className="text-base font-semibold text-slate-900 mb-4">Token Usage Distribution</h3>
            {patternsData.tokenDistribution && patternsData.tokenDistribution.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="55%" height={240}>
                  <PieChart>
                    <Pie
                      data={patternsData.tokenDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      dataKey="request_count"
                      paddingAngle={3}
                    >
                      {patternsData.tokenDistribution.map((_, i) => (
                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={v => [formatNumber(v), 'Requests']} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {patternsData.tokenDistribution.slice(0, 5).map((d, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
                      <span className="text-xs text-slate-600 flex-1">{d.token_range}</span>
                      <span className="text-xs font-medium text-slate-900 tabular-nums">{formatNumber(d.request_count)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
                No token distribution data available
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h3 className="text-base font-semibold text-slate-900 mb-4">Daily Usage Patterns</h3>
            {patternsData.dailyPatterns && patternsData.dailyPatterns.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={patternsData.dailyPatterns} margin={{ top: 4, right: 4, bottom: 0, left: 0 }} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="day_of_week" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                    tickFormatter={v => formatNumber(v)}
                  />
                  <Tooltip content={<ATooltip fmtVal={(v, n) => n === 'Tokens' ? formatNumber(v) : formatCurrency(v)} />} />
                  <Bar dataKey="total_tokens" fill="#6366f1" name="Tokens" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  <Bar dataKey="total_cost" fill="#10b981" name="Cost" radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
                No daily patterns available
              </div>
            )}
          </div>
        </div>
      )}

      {/* Forecasting */}
      {forecastData && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Usage Forecasting</h3>
          {forecastData.movingAverages && forecastData.movingAverages.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={forecastData.movingAverages} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="aFcActTokens" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.12} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="aFcActCost" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.12} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={v => new Date(v).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                    tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                    tickFormatter={v => formatNumber(v)}
                  />
                  <Tooltip content={<ATooltip fmtVal={(v, n) => n.includes('oken') ? formatNumber(v) : formatCurrency(v)} />} />
                  <Area type="monotone" dataKey="actual.tokens" stroke="#6366f1" strokeWidth={2}
                    fill="url(#aFcActTokens)" dot={false} name="Actual Tokens" />
                  <Area type="monotone" dataKey="movingAverage.tokens" stroke="#8b5cf6" strokeWidth={2}
                    strokeDasharray="6 3" fill="none" dot={false} name="Avg Tokens" />
                  <Area type="monotone" dataKey="actual.cost" stroke="#f59e0b" strokeWidth={2}
                    fill="url(#aFcActCost)" dot={false} name="Actual Cost" />
                  <Area type="monotone" dataKey="movingAverage.cost" stroke="#ef4444" strokeWidth={2}
                    strokeDasharray="6 3" fill="none" dot={false} name="Avg Cost" />
                </AreaChart>
              </ResponsiveContainer>
              <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-4 inline-block" style={{ borderBottom: '2px solid #6366f1' }} />
                  Actual Tokens
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 inline-block" style={{ borderBottom: '2px dashed #8b5cf6' }} />
                  7-day Avg Tokens
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 inline-block" style={{ borderBottom: '2px solid #f59e0b' }} />
                  Actual Cost
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 inline-block" style={{ borderBottom: '2px dashed #ef4444' }} />
                  7-day Avg Cost
                </span>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-48 text-slate-400 text-sm">
              No forecasting data available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Analytics;
